<template>
  <div>
    <!-- Loading Spinner -->
    <div v-if="isLoading || checkingBalance" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <!-- Table -->
    <div v-else class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th scope="col" class="px-6">Visit Code</th>
            <th scope="col" class="px-6">Patient Name</th>
            <th scope="col" class="px-6">Patient Unique Code</th>
            <th scope="col" class="px-6">Age</th>
            <th scope="col" class="px-6">Gender</th>
            <th scope="col" class="px-6">Registration Time</th>
            <th scope="col" class="px-6">Visit Type</th>
            <th scope="col" class="px-6">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(visit, index) in paginatedData" :key="index" class="border-b">
            <td class="px-6 py-2">{{ visit.VisitCode }}</td>
            <td class="px-6 py-2">{{ visit.patient.FullName }}</td>
            <td class="px-6 py-2">{{ visit.patient.unique_code }}</td>
            <td class="px-6 py-2">
              {{ visit.patient.Age }} {{ visit.patient.AgeType == 1 ? "Year(s)" : "Month(s)" }}
            </td>
            <td class="px-6 py-2">{{ visit.patient.Gender }}</td>
            <td class="px-6 py-2">{{ formatDateTime(visit.VisitStartTime) }}</td>
            <td class="px-6 py-2">
              <span :class="getVisitType(visit.VisitType)" class="px-3 py-1 rounded-full text-sm font-medium">
                {{ visit.VisitType }}
              </span>
            </td>
            <td class="px-6 py-2">
              <button
                @click="handleConsultationModal(visit.VisitId)"
                :disabled="checkingBalance"
                class="bg-custom-yellow hover:bg-yellow-600 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50"
              >
                Start Triage
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex justify-center gap-4 mt-8">
      <button @click="previousPage" :disabled="currentPage === 1" class="px-6 py-3 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50">
        Previous
      </button>
      <button v-for="page in totalPages" :key="page" @click="goToPage(page)"
        :class="{ 'bg-custom-blue text-white': currentPage === page, 'bg-gray-200': currentPage !== page }"
        class="w-10 h-10 rounded font-medium">
        {{ page }}
      </button>
      <button @click="nextPage" :disabled="currentPage === totalPages" class="px-6 py-3 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50">
        Next
      </button>
    </div>

    <!-- Consultation Modal -->
    <TriagePatientQueueModal
      :show="showConsultationModal"
      :visit="selectedVisit"
      :state="modalState"
      :loading="showLoader"
      @submit="handleSubmit"
      @close="closeConsultationModal"
      @refetch="fetchTriageVisits"
    />

    <!-- ORIGINAL Cash Payment Required Modal -->
    <div
      v-if="showCashPaymentWarning"
      class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
      role="dialog"
      aria-labelledby="cash-payment-warning-title"
      aria-modal="true"
    >
      <div class="bg-white rounded-lg shadow-md p-6 w-full max-w-md mx-4">
        <div class="flex items-center mb-4">
          <svg class="h-6 w-6 text-custom-yellow mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M12 4a8 8 0 100 16 8 8 0 000-16z" />
          </svg>
          <h2 id="cash-payment-warning-title" class="text-xl font-bold text-gray-700">
            Cash Payment Required
          </h2>
        </div>
        <p class="text-gray-600 mb-6">
          A cash payment of
          <span class="font-semibold">KES {{ formatPrice(outstandingBalance) }}</span>
          is required to proceed with triage.
        </p>
        <div class="flex justify-end gap-4">
          <button
            @click="closeAllModals"
            class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Insurance Eligible Modal -->
    <div
      v-if="showInsuranceEligibleModal"
      class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50"
      role="dialog"
      aria-labelledby="insurance-eligible-title"
      aria-modal="true"
    >
      <div
        class="bg-white rounded-lg shadow-md p-6 w-full max-w-md mx-4"
      >
        <div class="flex items-center mb-4">
          <svg
            class="h-6 w-6 text-green-500 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2
            id="insurance-eligible-title"
            class="text-xl font-bold text-gray-700"
          >
            Insurance Coverage Available
          </h2>
        </div>
        <p class="text-gray-600 mb-4">
          The patient is eligible for triage services under insurance coverage.
        </p>
        <div class="flex justify-end gap-4">
          <button
            @click="closeInsuranceEligibleModal"
            class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="proceedWithInsuranceTriage"
            class="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
          >
            Proceed with Triage
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import TriagePatientQueueModal from "@/views/modals/TriagePatientQueueModal.vue";
import { formatDateTime } from "@/services/customUtilities";

export default {
  name: "TriagePatientQueueTab",
  components: { TriagePatientQueueModal },
  props: { searchQuery: { type: String, default: "" } },
  data() {
    return {
      showConsultationModal: false,
      showProcedureModal: false,
      showBalanceWarning: false,
      showCashPaymentWarning: false,
      showInsuranceEligibleModal: false,
      checkingBalance: false,
      outstandingBalance: null,
      insuranceCoverageDetails: null,
      selectedVisitId: null,
      modalState: "create",
      visits: [],
      isLoading: false,
      currentPage: 1,
      itemsPerPage: 10,
      timer: null,
      showLoader: false,
    };
  },
  computed: {
    getVisitType() {
      return (type) => (type === "Consultation" ? "bg-green-200" : "bg-yellow-200");
    },
    totalPages() {
      return Math.ceil(this.visits.length / this.itemsPerPage) || 1;
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.visits.slice(start, start + this.itemsPerPage);
    },
    formatPrice() {
      return (val) => parseFloat(val || 0).toLocaleString("en-KE", { minimumFractionDigits: 2 });
    },
  },
  methods: {
    formatDateTime,
    async handleConsultationModal(visitId) {
      const visit = this.visits.find(v => v.VisitId === visitId);
      if (!visit) return this.$toast.error("Visit not found");

      this.selectedVisit = visit;
      this.checkingBalance = true;
      this.showCashPaymentWarning = false;

      try {
        // 1. Get bill
        const billRes = await apiClient.getBillByVisit(visitId);
        if (!billRes.success || !billRes.data?.id) throw new Error("Bill not found");
        const bill = billRes.data;

        // 2. Find the consultation/triage bill_item (billing_category: 1)
        const consultationBillItem = bill.bill_items?.find(item => item.billing_category === 1);
        if (!consultationBillItem) throw new Error("Consultation bill item not found");

        // 3. Use the actual facility_service_id and total_amount from the bill_item
        const serviceId = consultationBillItem.facility_service_id;
        const serviceCost = consultationBillItem.total_amount;

        // 4. Verify coverage using the real service_id
        const coverageRes = await apiClient.verifyTriageCoverage({
          patient_id: visit.patient.PatientId,
          visit_id: visitId,
          bill_id: bill.id,
          service_id: serviceId,
        });

        if (!coverageRes.success) throw new Error("Coverage check failed");

        const coverage = coverageRes.data;

        // 5. Check if patient has insurance payment method
        const hasInsurance = bill.bill_payment_methods?.some(method => method.payment_method_type === 'insurance');

        // 6. Insurance/MTIBA logic - show eligible modal if has insurance
        if (hasInsurance && coverage.is_covered) {
          this.insuranceCoverageDetails = {
            total_insurance_coverage: coverage.total_insurance_coverage || coverage.total_service_cost,
            total_service_cost: coverage.total_service_cost || serviceCost
          };
          this.selectedVisitId = visit;
          this.showInsuranceEligibleModal = true;
          this.checkingBalance = false;
          return;
        }

        // 7. FINAL LOGIC - Cash/M-Pesa/FFS
        if (coverage.is_covered || coverage.settle_at_end_allowed) {
          this.openTriageModal();
        } else {
          // Show the exact consultation/triage cost in the modal
          this.outstandingBalance = parseFloat(serviceCost || 0);
          this.showCashPaymentWarning = true;
        }

      } catch (err) {
        console.error("Coverage error:", err);
        this.$toast.error("Error checking coverage");
      } finally {
        this.checkingBalance = false;
      }
    },

    openTriageModal() {
      this.showConsultationModal = true;
      this.checkingBalance = false;
    },

    closeAllModals() {
      this.showCashPaymentWarning = false;
      this.checkingBalance = false;
      this.selectedVisit = null;
    },

    closeConsultationModal() {
      this.showConsultationModal = false;
      this.showProcedureModal = false;
      this.showBalanceWarning = false;
      this.showCashPaymentWarning = false;
      this.showInsuranceEligibleModal = false;
      this.outstandingBalance = null;
      this.insuranceCoverageDetails = null;
    },

    closeInsuranceEligibleModal() {
      this.showInsuranceEligibleModal = false;
      this.insuranceCoverageDetails = null;
    },

    proceedWithInsuranceTriage() {
      this.closeInsuranceEligibleModal();
      this.openTriageModal();
    },

    async fetchTriageVisits() {
      try {
        this.isLoading = true;
        const res = await apiClient.fetchTriageVisits();
        if (res.success) this.visits = res.data;
      } catch {
        this.$toast.error("Failed to load queue");
      } finally {
        this.isLoading = false;
      }
    },

    previousPage() { if (this.currentPage > 1) this.currentPage--; },
    nextPage() { if (this.currentPage < this.totalPages) this.currentPage++; },
    goToPage(p) { this.currentPage = p; },

    async handleSubmit(data) {
      this.showLoader = true;
      const res = await apiClient.saveTriage(data);
      this.showLoader = false;
      if (res.success) {
        this.$toast.success("Triage saved");
        this.closeConsultationModal();
        this.fetchTriageVisits();
      } else {
        this.$toast.error("Save failed");
      }
    },

    startPeriodicCalls() {
      this.fetchTriageVisits();
      this.timer = setInterval(() => this.fetchTriageVisits(), 180000);
    },
    stopPeriodicCalls() { if (this.timer) clearInterval(this.timer); },
  },
  created() { this.startPeriodicCalls(); },
  beforeUnmount() { this.stopPeriodicCalls(); },
};
</script>